// combatpro/spell/spellCastEngine.js
//
// Combat PRO - Spell Cast Engine (Stage 48A: pure spell-casting calculations).
//
// Pure, UI-independent spell-casting logic extracted from the spell execution
// runtime in battletracker.html (the read-only master spec). No React, no DOM, no
// context, no browser APIs, no side effects, no randomness (rolls are injected so
// the engine stays deterministic and reuses the Dice Runtime in the caller). It
// receives primitives / plain objects and returns plain data; it mutates nothing
// and owns no state.
//
// Decisions baked in (Stage 48A): the legacy safe-wrapper guard
// (_vi_insideSafeWrapper / VI_SPELL_ENGINE_STATS) and its console diagnostics are
// OMITTED - the React cast handler orchestrates a single scoped execution, so the
// anti-direct-call guard is unnecessary. SPELLS_DB stays external / injected (same
// as Stage 45A); it is never embedded here.
//
// Source mapping (battletracker.html):
//   - validateDamageFormula(formula)        vi_validateDamageFormula (4355-4366):
//                                           accepts a flat integer, XdY(+/-Z), or
//                                           N*(YdZ(+/-N)); else null.
//   - prepareSpellExecution(spell, caster)  vi_prepareSpellExecution (4407-4462):
//                                           clone, default execution fields,
//                                           type-lock to an allowed spellType,
//                                           validate damageFormula, assign priority,
//                                           flag reactive, apply monster
//                                           spellOverrides. Never mutates the input
//                                           or SPELLS_DB (operates on a shallow
//                                           clone). Diagnostics / stat counters
//                                           dropped per the Stage 48A decision.
//   - resolveSave(d20Roll, saveScore, dc)   the deterministic core of applySave
//                                           (4506-4509): total = d20Roll +
//                                           saveScore; dc defaults to 13; saved when
//                                           total >= dc. The d20 is injected (the
//                                           caller rolls it with the Dice Runtime).
//   - trackDuration(spellData, castRound, currentRound)
//                                           trackDuration (4593-4604): rounds left =
//                                           "Instant" when durationRounds is 0, else
//                                           max(0, (castRound + durationRounds) -
//                                           currentRound). castRound defaults to 1.
//   - buildSpellPreview(spell, targets)     vi_buildSpellPreview (11728-11767): pure
//                                           preview-data object (no DOM). The legacy
//                                           "no value" sentinel em dash is written
//                                           as "\u2014".
//
// NOT here (interface only / deferred): HP mutation (reuse the Damage Runtime
// applyDamage), morale (the Damage Runtime triggers it), effect writes (reuse
// updateEffect), condition normalisation (reuse normalizeCondition), dice
// (rollFormula / rollDie), the safe-wrapper, targeting (manual select, auto-select,
// player targets), preview render, cast buttons, combat log, last actions,
// save / cloud, AI, custom-spell CRUD, multi-stage effect logging. No optional
// chaining. No literal em dash.

// Allowed execution spell types and their execution priority (AI prep field kept
// for parity; not otherwise used in 48A).
var ALLOWED_SPELL_TYPES = ["damage", "control", "heal", "buff", "utility"];
var PRIORITY_MAP = { damage: 3, control: 4, heal: 5, buff: 2, utility: 1 };

// Validate a damage formula string. Returns the cleaned formula or null.
export function validateDamageFormula(formula) {
  if (!formula && formula !== 0) { return null; }
  const s = String(formula).replace(/\s/g, "");
  if (/^-?\d+$/.test(s)) { return s; }
  if (/^\d+d\d+([+-]\d+)?$/i.test(s)) { return s; }
  if (/^\d+\*\(\d+d\d+([+-]\d+)?\)$/i.test(s)) { return s; }
  return null;
}

// Build an execution-ready spell object: a shallow clone of the resolved spell
// with execution fields defaulted, the spellType locked to an allowed value, the
// damage formula validated, a priority assigned, reactive flagged, and any
// monster-level spellOverrides applied. Never mutates the input spell.
export function prepareSpellExecution(spell, casterMonster) {
  try {
    if (!spell) { return null; }
    const exec = Object.assign({}, spell);
    const defaults = {
      spellType: "utility", damageFormula: null, saveType: null,
      saveEffect: null, areaOfEffect: null, appliesCondition: null,
      durationRounds: 0, target: null
    };
    const defaultKeys = Object.keys(defaults);
    for (let i = 0; i < defaultKeys.length; i++) {
      const k = defaultKeys[i];
      if (exec[k] === undefined) { exec[k] = defaults[k]; }
    }
    if (ALLOWED_SPELL_TYPES.indexOf(exec.spellType) === -1) {
      exec.spellType = "utility";
    }
    if (exec.damageFormula !== null && exec.damageFormula !== undefined) {
      exec.damageFormula = validateDamageFormula(exec.damageFormula);
    }
    exec.priority = PRIORITY_MAP[exec.spellType] || 1;
    exec.hasReactive = !!(exec.reactiveDamageFormula);
    if (casterMonster && casterMonster.spellOverrides) {
      const overrides = casterMonster.spellOverrides[spell.name];
      if (overrides && typeof overrides === "object") {
        const allowed = ["target", "spellType", "damageFormula", "saveType", "saveEffect",
          "areaOfEffect", "appliesCondition", "durationRounds", "priority",
          "secondaryDamageFormula", "secondaryDamageDelayRounds",
          "ongoingDamageFormula", "ongoingDamageTrigger", "reactiveDamageFormula"];
        for (let j = 0; j < allowed.length; j++) {
          const k = allowed[j];
          if (k in overrides) { exec[k] = overrides[k]; }
        }
        if (overrides.damageFormula !== undefined) {
          exec.damageFormula = validateDamageFormula(exec.damageFormula);
        }
        exec.hasReactive = !!(exec.reactiveDamageFormula);
        if (!("priority" in overrides)) { exec.priority = PRIORITY_MAP[exec.spellType] || 1; }
      }
    }
    exec._prepared = true;
    return exec;
  } catch (e) {
    return spell ? Object.assign({}, spell) : null;
  }
}

// Resolve a saving throw. d20Roll is the injected raw d20 (1..20) from the Dice
// Runtime; saveScore is the target's save modifier; dc defaults to 13 (the legacy
// fallback). Returns the total, the dc used, and whether the save succeeded.
export function resolveSave(d20Roll, saveScore, dc) {
  const total = d20Roll + (saveScore || 0);
  const effectiveDc = dc || 13;
  return { total, dc: effectiveDc, saved: total >= effectiveDc };
}

// Rounds remaining for a spell's duration. "Instant" when durationRounds is 0;
// otherwise max(0, (castRound + durationRounds) - currentRound). castRound and
// currentRound default to 1, matching the legacy fallbacks.
export function trackDuration(spellData, castRound, currentRound) {
  if (!spellData) { return null; }
  const start = (castRound === undefined || castRound === null) ? 1 : castRound;
  const round = (currentRound === undefined || currentRound === null) ? 1 : currentRound;
  if (spellData.durationRounds === 0) { return "Instant"; }
  return Math.max(0, (start + spellData.durationRounds) - round);
}

// Build the pure preview-data object for a prepared / resolved spell. No DOM; the
// render layer (deferred to 48B) consumes this. The legacy "no value" sentinel em
// dash is written as "\u2014".
export function buildSpellPreview(spell, targets) {
  if (!spell) { return null; }
  const damageText = spell.damageFormula || (spell.damage && spell.damage !== "\u2014" ? spell.damage : null);
  const saveText = spell.saveType
    ? (spell.saveType + " Save \u2192 " + (spell.saveEffect === "half" ? "Half damage on success" : spell.saveEffect === "none" ? "No effect on success" : "Partial on success"))
    : null;
  let targeting = "Single Target";
  if (spell.target === "self") {
    targeting = "Self only";
  } else if (spell.areaOfEffect) {
    targeting = typeof spell.areaOfEffect === "string"
      ? ("AoE \u2014 " + spell.areaOfEffect)
      : ("AoE \u2014 " + spell.areaOfEffect.size + "ft " + spell.areaOfEffect.type);
  }
  const conditionText = spell.appliesCondition ? (spell.appliesCondition.charAt(0).toUpperCase() + spell.appliesCondition.slice(1)) : null;
  const durText = spell.durationRounds === 0 ? "Instantaneous"
    : spell.durationRounds === 9999 ? "Until dispelled"
    : spell.durationRounds != null ? (spell.durationRounds + " rounds" + (spell.concentration ? " (Conc.)" : ""))
    : (spell.duration || "");
  const secondaryText = spell.secondaryDamageFormula
    ? (spell.secondaryDamageFormula + (spell.secondaryDamageDelayRounds ? " after " + spell.secondaryDamageDelayRounds + "r" : "")) : null;
  const ongoingText = spell.ongoingDamageFormula
    ? (spell.ongoingDamageFormula + (spell.ongoingDamageTrigger ? " on " + spell.ongoingDamageTrigger : "")) : null;
  const reactiveText = spell.reactiveDamageFormula ? spell.reactiveDamageFormula : null;
  return {
    damage: damageText,
    save: saveText,
    targeting,
    conditions: conditionText,
    duration: durText,
    spellType: spell.spellType || "utility",
    targets: targets || [],
    targetOverride: spell.target || null,
    secondary: secondaryText,
    ongoing: ongoingText,
    reactive: reactiveText,
    hasReactive: !!(spell.hasReactive || spell.reactiveDamageFormula),
    priority: spell.priority || null
  };
}
